/**
 * VoiceOver — text-to-speech-skjelett basert på Web Speech API.
 *
 * Bygd i runde 43 for å gi de viktigste NPC-replikkene en stemme uten å
 * legge til eksterne API-avhengigheter. Web Speech API er native i alle
 * moderne nettlesere (Chrome, Safari, Edge, Firefox).
 *
 * Profiler:
 * - kristine: norsk kvinne, rate 0.95, pitch 1.05 — varm mentor
 * - haldis:   norsk kvinne, rate 0.9,  pitch 0.95 — streng redaktør
 * - bjorn:    norsk mann,   rate 0.85, pitch 0.9  — utbrent cynisk
 * - narrator: norsk nøytral, rate 1.0, pitch 1.0
 *
 * På/av lagres til localStorage. Default: AV (skjermlesere kolliderer
 * med voice-over; Sven kan skru på selv hvis ønsket).
 *
 * Runde 45:
 * - les() avbryter automatisk pågående tale før ny startes (interrupt-
 *   strategi i stedet for kø — enklere og snappiere for rask UI-flyt).
 * - Master mute via audioManager respekteres: hvis erBlokkertEksternt
 *   er satt, er les() en no-op selv om voice-over er PÅ i toggle.
 *   ForlagshusetScene kobler audioManager.erAv ↔ blokkering ved oppstart.
 */

const STORAGE_KEY = 'forlagshuset:voice-over-aktiv';

export type VoiceProfil = 'kristine' | 'haldis' | 'bjorn' | 'narrator';

interface ProfilKonfig {
  rate: number;
  pitch: number;
  /** Foretrukket kjønn — Web Speech API matcher på `voice.name`. */
  foretrekkerKjonn: 'kvinne' | 'mann' | 'noytral';
}

const PROFILER: Record<VoiceProfil, ProfilKonfig> = {
  kristine: { rate: 0.95, pitch: 1.05, foretrekkerKjonn: 'kvinne' },
  haldis: { rate: 0.9, pitch: 0.95, foretrekkerKjonn: 'kvinne' },
  bjorn: { rate: 0.85, pitch: 0.9, foretrekkerKjonn: 'mann' },
  narrator: { rate: 1.0, pitch: 1.0, foretrekkerKjonn: 'noytral' },
};

type Lytter = (aktiv: boolean) => void;

function lesAktivFraStorage(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function skrivAktivTilStorage(aktiv: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, aktiv ? '1' : '0');
  } catch {
    // ignore
  }
}

interface SpeechAPI {
  speak(utterance: SpeechSynthesisUtterance): void;
  cancel(): void;
  getVoices(): SpeechSynthesisVoice[];
  addEventListener?(type: string, listener: () => void): void;
}

export class VoiceOver {
  private aktiv = lesAktivFraStorage();
  private lyttere: Set<Lytter> = new Set();
  private voicesCache: SpeechSynthesisVoice[] = [];
  // Runde 45: ekstern blokkering — settes av AudioManager når master-
  // mute er på. Når true, er les() en no-op og pågående tale stoppes.
  private eksternBlokkering = false;

  /**
   * True hvis Web Speech API er tilgjengelig i denne runtime.
   * Returnerer false i jsdom (test) og eldre browsere.
   */
  erTilgjengelig(): boolean {
    if (typeof window === 'undefined') return false;
    const ss = (window as unknown as { speechSynthesis?: SpeechAPI }).speechSynthesis;
    if (!ss || typeof ss.speak !== 'function') return false;
    if (typeof SpeechSynthesisUtterance === 'undefined') return false;
    return true;
  }

  /** True hvis voice-over er skrudd på OG tilgjengelig OG ikke blokkert. */
  erAktiv(): boolean {
    return this.aktiv && !this.eksternBlokkering && this.erTilgjengelig();
  }

  /**
   * Runde 45: la andre subsystemer (typisk AudioManager) blokkere voice-
   * over uten å rote med den brukervalgte toggle-tilstanden. Settes til
   * true når master-mute er på; pågående tale stoppes umiddelbart.
   */
  settEksternBlokkering(blokkert: boolean): void {
    if (this.eksternBlokkering === blokkert) return;
    this.eksternBlokkering = blokkert;
    if (blokkert) this.stopp();
  }

  /** Returnerer true hvis ekstern blokkering er aktiv. */
  get erBlokkertEksternt(): boolean {
    return this.eksternBlokkering;
  }

  /** Toggle on/off, returnerer ny tilstand. */
  toggle(): boolean {
    this.aktiv = !this.aktiv;
    skrivAktivTilStorage(this.aktiv);
    if (!this.aktiv) this.stopp();
    this.varsel();
    return this.aktiv;
  }

  /** Eksplisitt sett på/av (uten toggle). */
  settAktiv(aktiv: boolean): void {
    if (this.aktiv === aktiv) return;
    this.aktiv = aktiv;
    skrivAktivTilStorage(this.aktiv);
    if (!this.aktiv) this.stopp();
    this.varsel();
  }

  /**
   * Les en tekst med gitt profil. No-op hvis voice-over er av eller
   * Web Speech API ikke er tilgjengelig.
   *
   * Runde 45: avbryter alltid pågående tale før ny startes. Dette
   * unngår overlapp når spilleren klikker raskt mellom modaler (f.eks.
   * Kristine → Haldis → Bjørn). Tidligere kunne tre stemmer overlappe.
   */
  les(tekst: string, profil: VoiceProfil = 'narrator'): void {
    if (!this.erAktiv()) return;
    if (typeof window === 'undefined') return;
    const ss = (window as unknown as { speechSynthesis?: SpeechAPI }).speechSynthesis;
    if (!ss) return;

    const trimmet = tekst.trim();
    if (trimmet.length === 0) return;

    // Auto-stopp pågående tale før ny starter. cancel() er trygt å kalle
    // selv om ingenting spilles av — Web Speech API garanterer no-op.
    try {
      ss.cancel();
    } catch {
      // ignore
    }

    try {
      const utterance = new SpeechSynthesisUtterance(trimmet);
      const konfig = PROFILER[profil];
      utterance.rate = konfig.rate;
      utterance.pitch = konfig.pitch;
      utterance.lang = 'nb-NO';

      const stemme = this.velgStemme(konfig.foretrekkerKjonn);
      if (stemme) {
        utterance.voice = stemme;
        utterance.lang = stemme.lang;
      }

      ss.speak(utterance);
    } catch (err) {
      console.warn('Voice-over feilet', err);
    }
  }

  /** Stopp pågående tale umiddelbart. */
  stopp(): void {
    if (typeof window === 'undefined') return;
    const ss = (window as unknown as { speechSynthesis?: SpeechAPI }).speechSynthesis;
    if (!ss) return;
    try {
      ss.cancel();
    } catch {
      // ignore
    }
  }

  /** Lytt til på/av-endringer. */
  subscribe(lytter: Lytter): () => void {
    this.lyttere.add(lytter);
    return () => this.lyttere.delete(lytter);
  }

  private velgStemme(
    foretrukket: 'kvinne' | 'mann' | 'noytral',
  ): SpeechSynthesisVoice | undefined {
    const stemmer = this.hentStemmer();
    if (stemmer.length === 0) return undefined;

    const norske = stemmer.filter(
      (v) => v.lang.startsWith('nb') || v.lang.startsWith('no'),
    );
    const pool = norske.length > 0 ? norske : stemmer;

    if (foretrukket === 'noytral') {
      return pool[0];
    }

    // Heuristisk navn-matching — Web Speech API har ikke kjønn-felt
    const kvinneHint = /(kvinne|female|woman|nora|sara|liv|ingrid|kari)/i;
    const mannHint = /(mann|male|man|henrik|lars|ole|knut|finn)/i;

    if (foretrukket === 'kvinne') {
      return (
        pool.find((v) => kvinneHint.test(v.name)) ??
        pool.find((v) => !mannHint.test(v.name)) ??
        pool[0]
      );
    }
    return (
      pool.find((v) => mannHint.test(v.name)) ??
      pool.find((v) => !kvinneHint.test(v.name)) ??
      pool[0]
    );
  }

  private hentStemmer(): SpeechSynthesisVoice[] {
    if (this.voicesCache.length > 0) return this.voicesCache;
    if (typeof window === 'undefined') return [];
    const ss = (window as unknown as { speechSynthesis?: SpeechAPI }).speechSynthesis;
    if (!ss) return [];
    try {
      const liste = ss.getVoices();
      this.voicesCache = liste;
      return liste;
    } catch {
      return [];
    }
  }

  private varsel(): void {
    for (const l of this.lyttere) {
      try {
        l(this.aktiv);
      } catch {
        // ignore
      }
    }
  }
}

export const voiceOver = new VoiceOver();
